import { useRoom } from '@/contexts/RoomContext'
import { usePeerConnection, useWebSocket } from '@/contexts/connections'
import { ControlBarProps, RootState, UserData } from '@/types'
import {
  faEye,
  faEyeSlash,
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
  faVideoSlash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useState } from 'react'
import useAuthUser from 'react-auth-kit/hooks/useAuthUser'
import { connect } from 'react-redux'

const sendersMap = new Map<string, RTCRtpSender[]>([
  ['video', []],
  ['screen', []],
  ['audio', []],
])

const ControlBar: React.FC<ControlBarProps> = () => {
  const roomContext = useRoom()
  const userData = useAuthUser<UserData>()
  const peerConnection = usePeerConnection()
  const ws = useWebSocket()

  const [isSharingScreen, setIsSharingScreen] = useState(false)

  const [isSharingMic, setIsSharingMic] = useState(false)

  const [isSharingCamera, setIsSharingCamera] = useState(false)

  const [screenStream, setScreenStream] = useState<MediaStream | null>()
  const [cameraStream, setCameraStream] = useState<MediaStream | null>()
  const [micStream, setMicStream] = useState<MediaStream | null>()

  const removeTrack = useCallback(
    async (type: string, track: MediaStreamTrack) => {
      const senders = sendersMap.get(type)
      if (!senders) return

      const senderToRemove = senders.find((sender) => sender.track === track)
      if (senderToRemove) {
        peerConnection?.connection?.removeTrack(senderToRemove)
        sendersMap.set(
          type,
          senders.filter((sender) => sender !== senderToRemove)
        )
        await peerConnection?.renegotiate(
          peerConnection.connection!,
          type,
          false
        )
      }
    },
    [peerConnection]
  )

  const stopSharing = useCallback(
    async (type: string) => {
      const senders = sendersMap.get(type)
      if (senders) {
        for (const sender of senders) {
          const track = sender.track
          if (!track) continue
          track.stop()
          await removeTrack(type, track)
        }
      }
    },
    [removeTrack]
  )

  async function addTrack(
    type: string,
    track: MediaStreamTrack,
    stream: MediaStream
  ) {
    if (!peerConnection) return

    const sender = peerConnection.connection?.addTrack(track, stream)
    if (!sender) return

    const existingSenders = sendersMap.get(type) || []
    sendersMap.set(type, [...existingSenders, sender])

    track.onended = () => {
      handleTrackEnded(type, track)
    }
    await peerConnection.renegotiate(peerConnection.connection!, type, true)
  }

  function handleTrackEnded(type: string, track: MediaStreamTrack) {
    removeTrack(type, track)

    if (type === 'screen') {
      setScreenStream(null)
      setIsSharingScreen(false)
    }
  }

  // Causes streaming to fail I think It reloads the componenet and ends the connection
  useEffect(() => {
    return () => {
      stopSharing('camera')
      setIsSharingCamera(false)
      stopSharing('screen')
      setIsSharingScreen(false)
      stopSharing('audio')
      setIsSharingMic(false)
    }
  }, [stopSharing])

  const stopScreenSharing = () => {
    stopSharing('screen')
    setScreenStream(null)
    setIsSharingScreen(false)
    roomContext?.toggleMediaState('screen', false)
  }

  const toggleScreenShare = async () => {
    if (isSharingScreen && screenStream) {
      stopScreenSharing()
    } else {
      try {
        if (!userData) return
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
        })

        for (const track of stream.getTracks()) {
          await addTrack('screen', track, stream)
          ws?.sendWebSocketMessage({
            type: 'track-metadata',
            track_type: 'screen',
            sender_id: userData?.user_id,
            track_id: stream?.getTracks()[0].id,
            stream_id: stream?.id,
          })
        }

        setScreenStream(stream)
        setIsSharingScreen(true)
        roomContext?.toggleMediaState('screen', true, stream)
      } catch (error) {
        console.error('Error sharing screen:', error)
      }
    }
  }

  const stopCameraSharing = () => {
    setCameraStream(null)
    setIsSharingCamera(false)
    stopSharing('video')
    roomContext?.toggleMediaState('video', false)
  }

  const toggleCamera = async () => {
    if (isSharingCamera && cameraStream) {
      stopCameraSharing()
    } else {
      try {
        if (!userData) return
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        })

        for (const track of stream.getVideoTracks()) {
          await addTrack('video', track, stream)
          ws?.sendWebSocketMessage({
            type: 'track-metadata',
            track_type: 'video',
            sender_id: userData?.user_id,
            track_id: stream?.getTracks()[0].id,
            stream_id: stream?.id,
          })
        }

        setCameraStream(stream)
        setIsSharingCamera(true)
        roomContext?.toggleMediaState('video', true, stream)
      } catch (error) {
        console.error('Error sharing camera:', error)
      }
    }
  }

  const toggleMicSharing = async () => {
    const senders = sendersMap.get('audio')
    if (senders) {
      for (const sender of senders) {
        const track = sender.track
        if (!track) continue
        track.enabled = !track.enabled
        ws?.sendWebSocketMessage({
          type: 'webrtc-tracks',
          sender_id: userData?.user_id,
          isMicEnabled: track.enabled,
        })
        roomContext?.toggleMediaState('audio', track.enabled)
        setIsSharingMic(track.enabled)
      }
    }
  }

  const toggleMicrophone = async () => {
    if (micStream) {
      toggleMicSharing()
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        })

        for (const track of stream.getAudioTracks()) {
          await addTrack('audio', track, stream)
          ws?.sendWebSocketMessage({
            type: 'track-metadata',
            track_type: 'audio',
            sender_id: userData?.user_id,
            track_id: stream?.getTracks()[0].id,
            stream_id: stream?.id,
          })
        }

        setMicStream(stream)
        setIsSharingMic(true)
        roomContext?.toggleMediaState('audio', true, stream)
      } catch (error) {
        console.error('Error accessing microphone:', error)
      }
    }
  }

  return (
    <div className="flex justify-center items-center gap-4 bg-gray-700 p-4 rounded-md mt-4">
      <button
        onClick={() => toggleMicrophone()}
        className={
          isSharingMic
            ? 'bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg'
            : 'bg-red-500 hover:bg-red-600 text-dark py-2 px-4 rounded-lg'
        }
      >
        {!isSharingMic && (
          <FontAwesomeIcon
            icon={faMicrophoneSlash}
            className="logo"
          ></FontAwesomeIcon>
        )}
        {isSharingMic && (
          <FontAwesomeIcon icon={faMicrophone} className="logo" />
        )}
      </button>
      <button
        onClick={() => toggleCamera()}
        className={
          isSharingCamera
            ? 'bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg'
            : 'bg-blue-500 hover:bg-blue-600 text-dark py-2 px-4 rounded-lg'
        }
      >
        {!isSharingCamera && (
          <FontAwesomeIcon
            icon={faVideoSlash}
            className="logo"
          ></FontAwesomeIcon>
        )}
        {isSharingCamera && <FontAwesomeIcon icon={faVideo} className="logo" />}
      </button>
      <button
        onClick={() => toggleScreenShare()}
        className={
          isSharingScreen
            ? 'bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg'
            : 'bg-green-500 hover:bg-green-600 text-dark py-2 px-4 rounded-lg'
        }
      >
        {!isSharingScreen && (
          <FontAwesomeIcon icon={faEyeSlash} className="logo"></FontAwesomeIcon>
        )}
        {isSharingScreen && <FontAwesomeIcon icon={faEye} className="logo" />}
      </button>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  currentRoomId: state.rooms.selectedRoomId,
})

const ConnectedControlBar = connect(mapStateToProps)(ControlBar)
export default ConnectedControlBar
